// src/services/api.ts
import { API_ENDPOINTS } from '../globalConstants';
import { handleApiError } from '../utils/errorHandler';

/**
 * Interface for queued request details
 */
interface QueuedRequest {
  url: string;
  options: RequestInit;
  timeout: number;
  resolve: (value: any) => void;
  reject: (error: any) => void;
}

/**
 * Generic API client for fetch-based requests.
 * - Accepts full URL (no baseURL logic inside the client)
 * - Allows passing custom headers and options from saga
 * - Automatically handles 401 errors with token refresh
 * - Queues and retries failed requests seamlessly
 */
class ApiClient {
  private _is_refreshing: boolean;
  private _refresh_promise: Promise<string | null> | null;
  private _failed_requests_queue: QueuedRequest[];
  private _current_token: string | null;

  constructor() {
    this._is_refreshing = false;
    this._refresh_promise = null;
    this._failed_requests_queue = [];
    this._current_token = null;

    // Bind methods to preserve context
    this._handle_401_refresh = this._handle_401_refresh.bind(this);
    this._handle_403_redirect = this._handle_403_redirect.bind(this);
    this._is_token_expired_error = this._is_token_expired_error.bind(this);
    this._process_queued_requests = this._process_queued_requests.bind(this);
    this._update_token_in_storages = this._update_token_in_storages.bind(this);
    this._clear_auth_data = this._clear_auth_data.bind(this);
    this.request = this.request.bind(this);
  }

  /**
   * Check if the error is due to token expiration
   */
  private _is_token_expired_error(error: any): boolean {
    if (error && typeof error === 'object') {
      return error.success === false && error.shouldRefresh;
    }

    if (typeof error === 'string') {
      try {
        const parsed = JSON.parse(error);
        return parsed.success === false && parsed.shouldRefresh;
      } catch {
        return false;
      }
    }

    return false;
  }

  /**
   * Update token in all storage locations
   */
  private async _update_token_in_storages(newToken: string): Promise<void> {
    this._current_token = newToken;
    console.log('token diff', this._current_token, newToken);

    localStorage.setItem('authToken', newToken);

    try {
      const { default: SessionDB } = await import('../utils/sessionStorage');
      SessionDB.setAccessToken(newToken);
    } catch (error) {
      // Silent fallback
    }

    try {
      const { updateAccessToken } = await import(
        '../containers/Auth/Login/actions'
      );
      const store = (window as any).__REDUX_STORE__ || (window as any).store;
      if (store) {
        store.dispatch(updateAccessToken(newToken));
      }
    } catch (error) {
      // Silent fallback
    }
  }

  /**
   * Clear all authentication data
   */
  private async _clear_auth_data(): Promise<void> {
    this._current_token = null;

    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    // Preserve country preference across logout
    const _savedCountry = localStorage.getItem('proflow_country');
    localStorage.clear();
    if (_savedCountry) localStorage.setItem('proflow_country', _savedCountry);

    try {
      const { default: SessionDB } = await import('../utils/sessionStorage');
      SessionDB.clear();
      SessionDB.clearRefreshTokenCookie();
    } catch (error) {
      // Silent fallback
    }
  }

  /**
   * Process the queue of failed requests with the new token
   */
  private async _process_queued_requests(
    error: any = null,
    token: string | null = null,
  ): Promise<void> {
    const queueToProcess = [...this._failed_requests_queue];
    this._failed_requests_queue = [];

    for (const queuedRequest of queueToProcess) {
      if (error) {
        queuedRequest.reject(error);
      } else if (token) {
        try {
          const updatedHeaders = {
            ...queuedRequest.options.headers,
            Authorization: `Bearer ${token}`,
          };

          const result = await this._execute_request<any>(
            queuedRequest.url,
            { ...queuedRequest.options, headers: updatedHeaders },
            queuedRequest.timeout,
            true,
          );

          queuedRequest.resolve(result);
        } catch (retryError) {
          queuedRequest.reject(retryError);
        }
      } else {
        queuedRequest.reject(new Error('Token refresh failed'));
      }
    }
  }

  /**
   * Handle 403 Forbidden responses by redirecting to login
   */
  private _handle_403_redirect(): void {
    // window.location.replace('/');
  }

  /**
   * Handle 401 Unauthorized by attempting token refresh
   */
  private async _handle_401_refresh(errorBody?: any): Promise<string | null> {
    if (!this._is_token_expired_error(errorBody)) {
      throw new Error(
        'Authentication failed - invalid credentials or other auth issue',
      );
    }

    if (this._is_refreshing && this._refresh_promise) {
      return this._refresh_promise;
    }

    this._is_refreshing = true;

    this._refresh_promise = this._perform_token_refresh(errorBody);

    try {
      const result = await this._refresh_promise;
      return result;
    } finally {
      this._is_refreshing = false;
      this._refresh_promise = null;
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async _perform_token_refresh(
    errorBody?: any,
  ): Promise<string | null> {
    try {
      const refreshResponse = await fetch(API_ENDPOINTS.auth.REFRESH_TOKEN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const refreshData = await refreshResponse.json();
      if (!refreshResponse.ok) {
        if (
          refreshData.message === 'Refresh token is missing' &&
          refreshData.accessToken === null
        ) {
          await this._clear_auth_data();
          window.location.replace('/');
        }

        throw new Error(
          `Refresh request failed with status: ${refreshResponse.status}`,
        );
      }

      if (refreshData.accessToken) {
        const newToken = refreshData.accessToken;

        await this._update_token_in_storages(newToken);
        await this._process_queued_requests(null, newToken);

        return newToken;
      } else {
        throw new Error('No access token in refresh response');
      }
    } catch (error) {
      await this._process_queued_requests(error);
      await this._clear_auth_data();
      // window.location.replace('/');

      return null;
    }
  }

  /**
   * Execute the actual fetch request
   */
  private async _execute_request<T>(
    url: string,
    options: RequestInit = {},
    timeout: number = 500 * 1000,
    isRetry: boolean = false,
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        credentials: 'include',
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401 && !isRetry) {
          let errorBody = null;
          try {
            const errorText = await response.text();
            errorBody = JSON.parse(errorText);
          } catch (error) {
            // Silent fallback
          }

          try {
            const newToken = await this._handle_401_refresh(errorBody);

            if (newToken) {
              const updatedHeaders = {
                ...options.headers,
                Authorization: `Bearer ${newToken}`,
              };

              return this._execute_request<T>(
                url,
                { ...options, headers: updatedHeaders, credentials: 'include' },
                timeout,
                true,
              );
            }
          } catch (refreshError: any) {
            if (
              refreshError.message?.includes(
                'invalid credentials or other auth issue',
              )
            ) {
              throw new Error(errorBody?.message || 'Authentication failed');
            }
            throw new Error('Authentication failed - redirecting to login');
          }
        }

        if (response.status === 403) {
          this._handle_403_redirect();
          throw new Error('Forbidden access - redirecting to login');
        }

        let errorBody = '';
        try {
          errorBody = await response.text();
        } catch {}

        throw new Error(
          errorBody || `HTTP ${response.status}: ${response.statusText}`,
        );
      }

      return this._formatResponse(response);

      // try {
      //   // return await response.json();
      //   return this._formatResponse(response);
      // } catch {
      //   // return (await response.text()) as any;
      //   return this._formatResponse(response);
      // }
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (
        !isRetry &&
        (error?.status === 401 || error?.response?.status === 401)
      ) {
        let errorBody = null;
        try {
          if (error?.response?.data) {
            errorBody = error.response.data;
          } else if (error?.message) {
            try {
              errorBody = JSON.parse(error.message);
            } catch {}
          }
        } catch (parseError) {
          // Silent fallback
        }

        try {
          const newToken = await this._handle_401_refresh(errorBody);

          if (newToken) {
            const updatedHeaders = {
              ...options.headers,
              Authorization: `Bearer ${newToken}`,
            };

            return this._execute_request<T>(
              url,
              { ...options, headers: updatedHeaders, credentials: 'include' },
              timeout,
              true,
            );
          }
        } catch (refreshError: any) {
          if (
            refreshError.message?.includes(
              'invalid credentials or other auth issue',
            )
          ) {
            throw error;
          }
          throw new Error('Authentication failed - redirecting to login');
        }
      }

      if (error?.status === 403 || error?.response?.status === 403) {
        this._handle_403_redirect();
        throw new Error('Forbidden access - redirecting to login');
      }

      handleApiError(error);
      throw error;
    }
  }

  async _formatResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type') || '';

    switch (true) {
      case contentType.includes('application/json'):
        return (await response.json()) as T;

      case contentType.includes('text/'):
        return (await response.text()) as unknown as T;

      case contentType.includes('application/octet-stream'):
      case contentType.includes('binary'):
        return (await response.arrayBuffer()) as unknown as T;
      default:
        // Unknown response type (possibly empty or HTML)
        try {
          const text = await response.text();
          return text as unknown as T;
        } catch {
          return null as T;
        }
    }
  }

  /**
   * Main request method that handles queuing for concurrent requests during token refresh
   */
  async request<T>(
    url: string,
    options: RequestInit = {},
    timeout: number = 500 * 1000,
    isRetry: boolean = false,
  ): Promise<T> {
    if (this._is_refreshing && !isRetry) {
      return new Promise<T>((resolve, reject) => {
        this._failed_requests_queue.push({
          url,
          options,
          timeout,
          resolve,
          reject,
        });
      });
    }

    return this._execute_request<T>(url, options, timeout, isRetry);
  }

  get<T>(url: string, options: RequestInit = {}, timeout?: number): Promise<T> {
    return this.request<T>(url, { ...options, method: 'GET' }, timeout);
  }

  post<T>(
    url: string,
    data: any,
    options: RequestInit = {},
    timeout?: number,
  ): Promise<T> {
    return this.request<T>(
      url,
      {
        ...options,
        method: 'POST',
        body: JSON.stringify(data),
        credentials: 'include',
      },
      timeout,
    );
  }

  put<T>(
    url: string,
    data: any,
    options: RequestInit = {},
    timeout?: number,
  ): Promise<T> {
    return this.request<T>(
      url,
      {
        ...options,
        method: 'PUT',
        body: JSON.stringify(data),
      },
      timeout,
    );
  }

  delete<T>(
    url: string,
    options: RequestInit = {},
    timeout?: number,
  ): Promise<T> {
    return this.request<T>(url, { ...options, method: 'DELETE' }, timeout);
  }

  /**
   * Get current refresh state (useful for debugging)
   */
  get isRefreshing(): boolean {
    return this._is_refreshing;
  }

  /**
   * Get current queue length (useful for debugging)
   */
  get queueLength(): number {
    return this._failed_requests_queue.length;
  }
}

const apiClient = new ApiClient();
export default apiClient;
